import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';

import ApiDocumentationCallToActionCard from './dependencies/ApiDocumentationCallToActionCard';
import FalabellaConfig from './dependencies/configurations/Falabella/FalabellaConfig';
import MercadoEnviosConfig from './dependencies/configurations/MercadoEnvíos/MercadoEnvíosConfig';
import MLFulfillConfig from './dependencies/configurations/MLFulfill/MLFulfillConfig';
import MLParcelConfig from './dependencies/configurations/MLParcel/MLParcelConfig';
import FalabellaConnector from './dependencies/connectors/FalabellaConnector';
import MercadoEnviosConnector from './dependencies/connectors/MercadoEnviosConnector';
import MLFulfillConnector from './dependencies/connectors/MLFulfillConnector';
import MLParcelConnector from './dependencies/connectors/MLParcelConnector';
import DocumentationPluginCard from './dependencies/DocumentationPluginCard';
import PluginCard from './dependencies/PluginCard';
import PluginGroupHeader from './dependencies/PluginGroupHeader';
import UpcomingPluginCard from './dependencies/UpcomingPluginCard';
import useMyPlugins from './useMyPlugins';

const PluginCardSkeleton = () => (
  <Paper elevation={3} className="relative flex h-28 w-full items-center justify-center">
    <Skeleton
      animation="wave"
      variant="rounded"
      width={100}
      height={24}
      sx={{ position: 'absolute', top: 10, left: 10 }}
    />
    <Skeleton animation="wave" variant="rounded" width={140} height={32} />
  </Paper>
);

const PLUGIN_GRID_CLASSES = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4';

const MyPluginsSection = () => {
  const hook = useMyPlugins();

  return (
    <div className="flex flex-col gap-8">
      <section>
        <div className={PLUGIN_GRID_CLASSES}>
          {hook.loading
            ? hook.configurablePlugins.map((plugin) => <PluginCardSkeleton key={plugin.id} />)
            : hook.configurablePlugins.map((plugin) => (
                <PluginCard
                  key={plugin.id}
                  name={plugin.name}
                  logo={plugin.logo}
                  logoStyle={plugin.logoStyle}
                  logoSuffix={plugin.logoSuffix}
                  status={plugin.status}
                  onConnect={hook.handleActiveConnector}
                  onDisconnect={() => hook.handleDisconnect(plugin.id)}
                  onUpdateConfig={hook.handleActiveConfiguration}
                />
              ))}
        </div>
      </section>

      <section>
        <PluginGroupHeader title="Otros plugins" />
        <div className={PLUGIN_GRID_CLASSES}>
          {hook.documentationPlugins.map((plugin) => (
            <DocumentationPluginCard
              key={plugin.id}
              name={plugin.name}
              logo={plugin.logo}
              logoStyle={plugin.logoStyle}
              documentation_url={plugin.documentation_url}
            />
          ))}
          <ApiDocumentationCallToActionCard />
        </div>
      </section>

      {hook.upcomingPlugins.length > 0 && (
        <section>
          <PluginGroupHeader title="Próximamente nuevas versiones" badge="En camino" />
          <div className={PLUGIN_GRID_CLASSES}>
            {hook.upcomingPlugins.map((plugin) => (
              <UpcomingPluginCard
                key={plugin.id}
                name={plugin.name}
                logo={plugin.logo}
                logoStyle={plugin.logoStyle}
              />
            ))}
          </div>
        </section>
      )}

      {/* Connectors */}
      <MercadoEnviosConnector
        id="mercado-envios"
        name="Mercado Libre Flex"
        isOpen={hook.activeConnector === 'Mercado Libre Flex'}
        onClose={() => hook.handleActiveConnector('')}
        onConnect={hook.handleConnect}
      />
      <MLFulfillConnector
        id="mercado-envios-fulfill99"
        name="Mercado Libre Fulfill99"
        isOpen={hook.activeConnector === 'Mercado Libre Fulfill99'}
        onClose={() => hook.handleActiveConnector('')}
        onConnect={hook.handleConnect}
      />
      <MLParcelConnector
        id="mercado-parcel"
        name="Mercado Libre Parcel"
        isOpen={hook.activeConnector === 'Mercado Libre Parcel'}
        onClose={() => hook.handleActiveConnector('')}
        onConnect={hook.handleConnect}
      />
      <FalabellaConnector
        id="falabella"
        name="Falabella"
        clientId={hook.clientId}
        isOpen={hook.activeConnector === 'Falabella'}
        onClose={() => hook.handleActiveConnector('')}
        onConnect={() => {
          hook.fetchUserPlugins();
        }}
      />

      {/* Configurations */}
      <MercadoEnviosConfig
        name="Mercado Libre Flex"
        isOpen={hook.activeConfiguration === 'Mercado Libre Flex'}
        onClose={() => hook.handleActiveConfiguration('')}
        pluginId={hook.mercadoPluginId}
        clientId={hook.clientId}
        activeMlCount={hook.activeMlCount}
        webhookPlatformOwner={hook.webhookPlatformOwner}
        onSaved={hook.fetchUserPlugins}
      />
      <MLFulfillConfig
        name="Mercado Libre Fulfill99"
        isOpen={hook.activeConfiguration === 'Mercado Libre Fulfill99'}
        onClose={() => hook.handleActiveConfiguration('')}
        pluginId={hook.mlFulfillPluginId}
        clientId={hook.clientId}
        activeMlCount={hook.activeMlCount}
        webhookPlatformOwner={hook.webhookPlatformOwner}
        onSaved={hook.fetchUserPlugins}
      />
      <MLParcelConfig
        name="Mercado Libre Parcel"
        isOpen={hook.activeConfiguration === 'Mercado Libre Parcel'}
        onClose={() => hook.handleActiveConfiguration('')}
        pluginId={hook.mlParcelPluginId}
        clientId={hook.clientId}
        activeMlCount={hook.activeMlCount}
        webhookPlatformOwner={hook.webhookPlatformOwner}
        onSaved={hook.fetchUserPlugins}
      />
      <FalabellaConfig
        name="Falabella"
        isOpen={hook.activeConfiguration === 'Falabella'}
        onClose={() => hook.handleActiveConfiguration('')}
        pluginId={hook.falabellaPluginId}
        clientId={hook.clientId}
        onSaved={hook.fetchUserPlugins}
      />
    </div>
  );
};

export default MyPluginsSection;
